
import { Suspense } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ArkCareFoundation() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section 
          className="relative pt-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/bdf2a8aa5001d70cbe3623fba2f61219.jpg)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-700/80"></div>
          <div className="relative max-w-7xl mx-auto px-6 py-32">
            <div className="max-w-3xl">
              <div className="mb-6 flex justify-start">
                <img 
                  src="https://static.readdy.ai/image/abcfde42a70d25526c2b82811ba21d87/a302c9bf9cf66df2dc03ae7bf3679d71.jpeg" 
                  alt="ARK CARE FOUNDATION Logo" 
                  className="h-14 w-auto object-contain"
                />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                ARK CARE FOUNDATION
              </h1>
              <p className="text-2xl md:text-3xl text-yellow-400 font-semibold mb-8">
                Empowering Lives, Transforming Communities
              </p>
              <p className="text-xl text-white/90 leading-relaxed mb-10">
                A non-profit organization dedicated to supporting vulnerable individuals in our community, including orphans, widows, and intelligent students in need.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="#get-involved" 
                  className="px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Get Involved
                </a>
                <a 
                  href="#about" 
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border-2 border-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                About <span className="text-blue-700">ARK CARE FOUNDATION</span>
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                ARK CARE FOUNDATION is a non-profit organization dedicated to supporting vulnerable individuals in our community, including orphans, widows, and intelligent students in need. Our approach focuses on empowering individuals and families, rather than institutionalizing them.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Happy%20African%20children%20and%20families%20in%20a%20community%20center%20learning%20and%20growing%20together%20with%20warm%20natural%20lighting%20showing%20education%20support%20and%20empowerment%20in%20a%20clean%20modern%20setting%20with%20simple%20background%20featuring%20African%20students%20studying%20and%20smiling%20with%20books%20and%20learning%20materials&width=800&height=600&seq=ark-about-african-001&orientation=landscape" 
                  alt="Community Support" 
                  className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  To stand up and support the needy people in our society, providing them with the resources and opportunities they need to thrive. We believe in creating lasting change through community-based support and individual empowerment.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mr-4 flex-shrink-0">
                      <i className="ri-heart-fill text-2xl text-blue-700"></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Compassion</h4>
                      <p className="text-gray-600">We approach every individual with empathy and understanding</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full mr-4 flex-shrink-0">
                      <i className="ri-hand-heart-fill text-2xl text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Empowerment</h4>
                      <p className="text-gray-600">We enable individuals to become self-sufficient and independent</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mr-4 flex-shrink-0">
                      <i className="ri-community-fill text-2xl text-green-700"></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Community</h4>
                      <p className="text-gray-600">We believe in the power of community-based support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Objectives Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our <span className="text-blue-700">Objectives</span>
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                  <i className="ri-book-open-fill text-3xl text-blue-700"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Educational Support</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Provide educational support to intelligent students in need, helping them achieve their academic dreams
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-100 rounded-full mb-6 mx-auto">
                  <i className="ri-user-heart-fill text-3xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Empower Widows</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Empower widows and single mothers through vocational training and economic empowerment programs
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-6 mx-auto">
                  <i className="ri-parent-fill text-3xl text-green-700"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Support Orphans</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Support orphans and vulnerable children through community-based care and family reintegration programs
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mb-6 mx-auto">
                  <i className="ri-team-fill text-3xl text-purple-700"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Community Engagement</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Foster a culture of community engagement and social responsibility across all our programs
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our <span className="text-blue-700">Approach</span>
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto mb-8"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-10">
              <div className="text-center">
                <div className="relative mb-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=African%20community%20gathering%20with%20diverse%20African%20people%20supporting%20each%20other%20in%20a%20neighborhood%20setting%20showing%20unity%20and%20collaboration%20with%20warm%20natural%20lighting%20and%20simple%20clean%20background%20featuring%20African%20families%20and%20community%20members%20working%20together%20in%20traditional%20and%20modern%20African%20setting&width=600&height=400&seq=ark-approach-african-001&orientation=landscape" 
                    alt="Community-based Support" 
                    className="w-full h-64 object-cover object-top rounded-2xl shadow-lg"
                  />
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-16 h-16 flex items-center justify-center bg-blue-700 rounded-full shadow-xl">
                    <i className="ri-community-line text-3xl text-white"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Community-Based Support</h3>
                <p className="text-gray-600 leading-relaxed">
                  We believe that individuals and families are best supported within their own communities, maintaining their dignity and connections
                </p>
              </div>

              <div className="text-center">
                <div className="relative mb-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Empowered%20African%20women%20and%20individuals%20learning%20new%20skills%20in%20a%20training%20center%20showing%20confidence%20and%20growth%20with%20bright%20natural%20lighting%20and%20modern%20clean%20setting%20with%20simple%20background%20featuring%20African%20people%20in%20vocational%20training%20and%20skill%20development%20programs%20with%20tools%20and%20materials&width=600&height=400&seq=ark-approach-african-002&orientation=landscape" 
                    alt="Empowerment" 
                    className="w-full h-64 object-cover object-top rounded-2xl shadow-lg"
                  />
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-16 h-16 flex items-center justify-center bg-yellow-500 rounded-full shadow-xl">
                    <i className="ri-lightbulb-flash-line text-3xl text-gray-900"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Empowerment</h3>
                <p className="text-gray-600 leading-relaxed">
                  We empower individuals to become self-sufficient and independent, providing tools and resources for lasting change
                </p>
              </div>

              <div className="text-center">
                <div className="relative mb-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20African%20professionals%20and%20volunteers%20working%20together%20in%20partnership%20showing%20collaboration%20and%20teamwork%20with%20natural%20lighting%20and%20clean%20modern%20background%20featuring%20African%20people%20in%20community%20service%20and%20nonprofit%20work%20helping%20others%20with%20compassion&width=600&height=400&seq=ark-approach-african-003&orientation=landscape" 
                    alt="Collaboration" 
                    className="w-full h-64 object-cover object-top rounded-2xl shadow-lg"
                  />
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-16 h-16 flex items-center justify-center bg-green-600 rounded-full shadow-xl">
                    <i className="ri-hand-heart-line text-3xl text-white"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Collaboration</h3>
                <p className="text-gray-600 leading-relaxed">
                  We work with local organizations, government agencies, and individuals to amplify our impact and reach more people
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Our <span className="text-yellow-400">Values</span>
              </h2>
              <div className="w-24 h-1 bg-yellow-400 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mb-4 mx-auto">
                  <i className="ri-heart-3-fill text-4xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Compassion</h3>
                <p className="text-white/80 text-sm">Caring deeply for those we serve</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mb-4 mx-auto">
                  <i className="ri-emotion-happy-fill text-4xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Empathy</h3>
                <p className="text-white/80 text-sm">Understanding and sharing feelings</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mb-4 mx-auto">
                  <i className="ri-rocket-fill text-4xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Empowerment</h3>
                <p className="text-white/80 text-sm">Enabling self-sufficiency</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mb-4 mx-auto">
                  <i className="ri-group-fill text-4xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Community</h3>
                <p className="text-white/80 text-sm">Building stronger connections</p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mb-4 mx-auto">
                  <i className="ri-shield-check-fill text-4xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Integrity</h3>
                <p className="text-white/80 text-sm">Honesty in all we do</p>
              </div>
            </div>
          </div>
        </section>

        {/* Get Involved Section */}
        <section id="get-involved" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get <span className="text-blue-700">Involved</span>
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Join us in our mission to make a difference in the lives of those who need it most. Whether you're an individual, organization, or business, we welcome your support and partnership.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-700 rounded-full mb-6 mx-auto">
                  <i className="ri-hand-coin-fill text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Donate</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Your financial contribution helps us provide essential support to those in need
                </p>
                <a 
                  href="mailto:acf@gmail.com?subject=Donation Inquiry" 
                  className="inline-block px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Make a Donation
                </a>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-8 rounded-2xl text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-500 rounded-full mb-6 mx-auto">
                  <i className="ri-time-fill text-3xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Volunteer</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Share your time and skills to directly impact lives in your community
                </p>
                <a 
                  href="mailto:acf@gmail.com?subject=Volunteer Inquiry" 
                  className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Become a Volunteer
                </a>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-full mb-6 mx-auto">
                  <i className="ri-building-fill text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Partner</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Collaborate with us as an organization or business to amplify our impact
                </p>
                <a 
                  href="mailto:acf@gmail.com?subject=Partnership Inquiry" 
                  className="inline-block px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Partner With Us
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-3xl p-12 text-center text-white">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                Together, let's stand up and support the needy people in our society!
              </h3>
              <p className="text-xl mb-8 text-white/90">
                Every contribution, big or small, makes a meaningful difference
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="mailto:acf@gmail.com" 
                  className="px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap inline-flex items-center"
                >
                  <i className="ri-mail-fill text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
